import { Box } from "@mui/material";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useAppMe } from "../../services/okta/index";
import { CircularProgressCustom } from "../../utils/muiCustomTheme";
import LeftNavigation from "./left-navigation/LeftNavigation";

/**
 * A wrapper component that protects routes requiring authentication.
 *
 * This component checks if the user is authenticated using the `useAppMe` hook.
 *
 * Behavior:
 * 1. **Loading**: Displays a loading spinner while the authentication status is being determined.
 * 2. **Unauthenticated**:
 *    - Saves the current URL to `sessionStorage` (key: "redirectUrl") to allow post-login redirection.
 *    - Redirects the user to the `/login` page.
 * 3. **Authenticated**:
 *    - Checks if a "redirectUrl" exists in `sessionStorage`.
 *    - If found and different from the current URL, redirects the user to the saved URL and clears the storage.
 *    - If no redirect is needed, renders the main application layout (Sidebar + Outlet).
 *
 * Note: The `useAppMe` hook is called with `{ retry: false }` to prevent multiple API calls
 * and long waiting times when the user is not authenticated (401).
 *
 * @returns {JSX.Element} The protected layout or a redirect/loading component.
 */
export default function ProtectedRoute() {
  const { data: dataAppMe, isPending: isPendingAppMe } = useAppMe({ options: { retry: false } });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (dataAppMe?.data?.user_id) {
      const redirectUrl = sessionStorage.getItem("redirectUrl");
      const currentUrl = location.pathname + location.search;

      if (redirectUrl) {
        if (redirectUrl !== currentUrl) {
          navigate(redirectUrl, { replace: true });
        }
        sessionStorage.removeItem("redirectUrl");
      }
    }
  }, [dataAppMe, navigate, location]);

  const redirectUrl = sessionStorage.getItem("redirectUrl");
  const isWaitingRedirect =
    !!dataAppMe?.data?.user_id &&
    !!redirectUrl &&
    redirectUrl !== (location.pathname + location.search);

  if (isPendingAppMe || isWaitingRedirect) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}
      >
        <CircularProgressCustom />
      </Box>
    );
  }

  if (!dataAppMe?.data?.user_id) {
    if (!redirectUrl) {
      sessionStorage.setItem("redirectUrl", location.pathname + location.search);
    }
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-wrapper">
      <div className="app-wrapper__sidebar">
        <LeftNavigation />
        <div className="app-wrapper__body-wrap">
          <article>
            <Outlet />
          </article>
        </div>
      </div>
    </div>
  );
}
